/**
 * useScheduleIngestionRun — the create -> upload/start -> poll ingestion run,
 * lifted out of ScheduleIngestionModal so the Build Itinerary wizard's Step 1
 * can drive the same flow without reinventing it.
 *
 * A run is either a pasted URL (create -> poll; the server enqueues immediately,
 * no upload and no /start) or an uploaded file (create -> upload -> start ->
 * poll). The hook owns a single AbortController for the whole run so unmount or
 * a fresh run aborts every in-flight request. The polling loop, timeouts, and
 * failure-count semantics match the modal exactly (see the comments there).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getScheduleIngestionStatus,
  startScheduleIngestion,
  startScheduleIngestionFromUrl,
  uploadScheduleFiles,
} from '../../../../../services/schedule-ingestions';
import { getSafeErrorMessage } from '../../../../../utils/errorHandler';
import type { ScheduleFetchMode } from '../../../../../types/schedule-ingestion';
import {
  FRIENDLY_FALLBACK,
  URL_FRIENDLY_FALLBACK,
  progressLabelForStatus,
} from '../scheduleIngestionShared';

const POLL_INTERVAL_MS = 3000;
// Big PDFs/images can take several minutes to extract server-side, so wait
// generously before giving up. Kept above the server model-call timeout
// (VertexAi:TimeoutMinutes) so the UI doesn't quit while extraction is still running.
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const STATUS_REQUEST_TIMEOUT_MS = 10000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

/** The phase of an ingestion run, for driving Step 1's UI. */
export type IngestionRunPhase =
  | 'idle'
  | 'uploading'
  | 'extracting'
  | 'completed'
  | 'empty'
  | 'failed';

export interface IngestionRunState {
  phase: IngestionRunPhase;
  /** A friendly progress label while extracting, e.g. "Reading the link...". */
  progressLabel: string;
  /** A friendly error message when the phase is "failed". */
  error: string;
  /** The ingestion id, available once the run has been created. */
  ingestionId: string;
}

const IDLE_STATE: IngestionRunState = {
  phase: 'idle',
  progressLabel: '',
  error: '',
  ingestionId: '',
};

export interface UseScheduleIngestionRun {
  state: IngestionRunState;
  /** Start a run from one uploaded PDF/image file. */
  runFile: (file: File) => Promise<void>;
  /** Start a run from several uploaded PDF/image files (imported into one schedule). */
  runFiles: (files: File[]) => Promise<void>;
  /** Start a run from a pasted schedule URL. */
  /**
   * Read a pasted URL. `fetchMode` omitted means the server's default plain GET;
   * 'Rendered' asks for headless Chromium and is refused with 403 unless the
   * caller is a system admin.
   */
  runUrl: (sourceUrl: string, fetchMode?: ScheduleFetchMode) => Promise<void>;
  /** Reset back to idle (e.g. when the user retries or picks a new source). */
  reset: () => void;
}

export function useScheduleIngestionRun(): UseScheduleIngestionRun {
  const [state, setState] = useState<IngestionRunState>(IDLE_STATE);

  // One controller for the whole create -> upload -> start -> poll run so
  // unmount or a new run aborts every in-flight request.
  const runControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      runControllerRef.current?.abort();
    };
  }, []);

  const pollUntilDone = useCallback(
    (ingestionId: string, signal: AbortSignal) =>
      new Promise<void>((resolve) => {
        const deadline = Date.now() + POLL_TIMEOUT_MS;
        let consecutiveFailures = 0;

        const poll = async () => {
          if (signal.aborted) {
            resolve();
            return;
          }

          if (Date.now() > deadline) {
            setState((prev) => ({
              ...prev,
              phase: 'failed',
              error:
                'This is taking longer than expected. Please try again.',
            }));
            resolve();
            return;
          }

          try {
            const status = await getScheduleIngestionStatus(
              ingestionId,
              signal,
              STATUS_REQUEST_TIMEOUT_MS
            );
            if (signal.aborted) {
              resolve();
              return;
            }

            consecutiveFailures = 0;

            if (status.status === 'Completed') {
              const found = status.games ?? [];
              setState((prev) => ({
                ...prev,
                phase: found.length === 0 ? 'empty' : 'completed',
              }));
              resolve();
              return;
            }

            if (status.status === 'Failed') {
              setState((prev) => ({
                ...prev,
                phase: 'failed',
                error: status.error || FRIENDLY_FALLBACK,
              }));
              resolve();
              return;
            }

            setState((prev) => ({
              ...prev,
              phase: 'extracting',
              progressLabel: progressLabelForStatus(status.status),
            }));
          } catch {
            if (signal.aborted) {
              resolve();
              return;
            }
            consecutiveFailures += 1;
            if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
              setState((prev) => ({
                ...prev,
                phase: 'failed',
                error:
                  'We are having trouble reaching the server. Check your connection and try again.',
              }));
              resolve();
              return;
            }
          }

          setTimeout(poll, POLL_INTERVAL_MS);
        };

        void poll();
      }),
    []
  );

  const beginRun = useCallback((): AbortSignal => {
    runControllerRef.current?.abort();
    const controller = new AbortController();
    runControllerRef.current = controller;
    return controller.signal;
  }, []);

  const runFiles = useCallback(
    async (files: File[]) => {
      const signal = beginRun();
      setState({
        phase: 'uploading',
        progressLabel: '',
        error: '',
        ingestionId: '',
      });

      try {
        const ingestionId = await uploadScheduleFiles(files, signal);
        if (signal.aborted) return;
        setState((prev) => ({
          ...prev,
          ingestionId,
          phase: 'extracting',
          progressLabel: progressLabelForStatus('Extracting'),
        }));

        await startScheduleIngestion(ingestionId, signal);
        if (signal.aborted) return;

        await pollUntilDone(ingestionId, signal);
      } catch (err) {
        if (signal.aborted) return;
        setState((prev) => ({
          ...prev,
          phase: 'failed',
          error: getSafeErrorMessage(err, FRIENDLY_FALLBACK),
        }));
      }
    },
    [beginRun, pollUntilDone]
  );

  const runFile = useCallback(
    (file: File) => runFiles([file]),
    [runFiles]
  );

  const runUrl = useCallback(
    async (sourceUrl: string, fetchMode?: ScheduleFetchMode) => {
      const signal = beginRun();
      setState({
        phase: 'extracting',
        progressLabel: progressLabelForStatus('Fetching'),
        error: '',
        ingestionId: '',
      });

      try {
        const ingestionId = await startScheduleIngestionFromUrl(
          sourceUrl,
          signal,
          fetchMode
        );
        if (signal.aborted) return;
        setState((prev) => ({ ...prev, ingestionId }));

        await pollUntilDone(ingestionId, signal);
      } catch (err) {
        if (signal.aborted) return;
        setState((prev) => ({
          ...prev,
          phase: 'failed',
          error: getSafeErrorMessage(err, URL_FRIENDLY_FALLBACK),
        }));
      }
    },
    [beginRun, pollUntilDone]
  );

  const reset = useCallback(() => {
    runControllerRef.current?.abort();
    setState(IDLE_STATE);
  }, []);

  return { state, runFile, runFiles, runUrl, reset };
}
